import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStudyPrefetch } from '@/state/hooks/useStudyPrefetch';
import { StudyPropertiesModal } from '@/features/study-detail';
import type { TimelineStudy } from '../../types/studyTimeline';
import { TimelineDot } from './TimelineDot';
import { StudyCardHeader } from './StudyCardHeader';
import { StudyCardMeta } from './StudyCardMeta';
import { StudyCardAccession } from './StudyCardAccession';
import { ReportBadges } from './ReportBadges';
import { ReportInlineToggle } from './ReportInlineToggle';
import { ReportHistorySubRail } from './ReportHistorySubRail';
import { StudyCardActions } from './StudyCardActions';
import useStudyInfoModal from './hooks/useStudyInfoModal';
import styles from './StudyCard.module.css';

interface StudyCardProps {
  study: TimelineStudy;
  isLastGlobal: boolean;
  expanded: boolean;
  onToggle: (uuid: string) => void;
}

export default function StudyCard({
  study,
  isLastGlobal,
  expanded,
  onToggle,
}: StudyCardProps) {
  const navigate = useNavigate();
  const prefetchStudy = useStudyPrefetch();
  const hasReports = study.reports.length > 0;
  const hasDraft = study.reports.some((r) => r.status === 'DRAFT');
  const { open: studyInfoOpen, openModal, closeModal } = useStudyInfoModal();

  const handleToggle = useCallback(
    () => onToggle(study.uuid),
    [onToggle, study.uuid]
  );

  const handleOpenStudy = useCallback(() => {
    navigate(`/studies/${study.uuid}`);
  }, [navigate, study.uuid]);

  const handleOpenReport = useCallback(() => {
    navigate(`/reports/${study.uuid}/preview`);
  }, [navigate, study.uuid]);

  const handleStudyInfo = useCallback(() => {
    openModal();
  }, [openModal]);

  const handleOpenVersion = useCallback(
    (_uuid: string) => {
      navigate(`/reports/${study.uuid}/preview`);
    },
    [navigate, study.uuid]
  );

  return (
    <div className={styles.card} onMouseEnter={() => prefetchStudy(study.uuid)}>
      <TimelineDot isLatest={study.isLatest} showLine={!isLastGlobal} />
      <div className={styles.body}>
        <div className={styles.topRow}>
          <StudyCardHeader
            studyTypeName={study.studyTypeName}
            isLatest={study.isLatest}
            hasDraft={hasDraft}
          />
          <ReportBadges reports={study.reports} />
        </div>

        <StudyCardMeta
          studyDateTime={study.studyDateTime}
          readingPhysicianName={study.readingPhysicianName}
        />

        <StudyCardAccession
          accessionNumber={study.accessionNumber}
          orderNumber={study.orderNumber}
          referringPhysicianName={study.referringPhysicianName}
          referringPhysicianDepartment={study.referringPhysicianDepartment}
          sendStatus={study.sendStatus}
        />

        {hasReports && (
          <ReportInlineToggle
            reports={study.reports}
            expanded={expanded}
            onToggle={handleToggle}
          />
        )}

        <ReportHistorySubRail
          expanded={expanded}
          versions={study.reports}
          onOpenVersion={handleOpenVersion}
        />

        <StudyCardActions
          hasReport={hasReports}
          onOpenStudy={handleOpenStudy}
          onOpenReport={handleOpenReport}
          onStudyInfo={handleStudyInfo}
        />

        <StudyPropertiesModal
          open={studyInfoOpen}
          onClose={closeModal}
          studyUuid={study.uuid}
          reportUuid={study.reports[0]?.uuid}
        />
      </div>
    </div>
  );
}
